import { collection, getDocs, doc, setDoc, onSnapshot, deleteDoc, writeBatch } from 'firebase/firestore';
import { db } from '../../firebase';
import { createLogger } from '../../utils/logger.ts';

// ========================================
// Logger 설정 - EmployeeFirebaseService
// ========================================
const logger = createLogger('EmployeeFirebaseService');
const DEBUG = false;

// 직원 데이터 캐시
let employeesCache: any = null;
let employeesCacheTime = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5분

// 캐시된 직원 데이터 가져오기 (캐시 비활성화 - 디버깅용)
export const getCachedEmployees = async () => {
  // 🔧 캐시 비활성화하여 항상 새로운 데이터 로드
  DEBUG && logger.debug('캐시 비활성화 - 새로운 직원 데이터 로드');
  const freshEmployees = await getEmployees();
  // 깊은 복사로 반환하여 참조 공유 방지
  return freshEmployees ? JSON.parse(JSON.stringify(freshEmployees)) : freshEmployees;
};

// 직원 정보 가져오기
export const getEmployees = async () => {
  const employeeSnapshot = await getDocs(collection(db, 'employees'));
  return employeeSnapshot.docs.map(doc => ({
    name: doc.id,
    ...doc.data()
  }));
};

// 🛡️ 직원 문서 ID(= 직원 이름) 유효성 검사
// Firestore 문서 ID 제약: 빈 문자열 불가, '/' 포함 불가(경로 구분자), '.'·'..' 불가,
//   __...__ 예약 패턴 불가. 하나라도 위반하면 doc() 생성 시 동기 예외가 나서
//   writeBatch 전체가 실패한다(= 정상 직원까지 저장/삭제 안 됨). 이런 불량 레코드는
//   조용히 건너뛰고 경고만 남겨, 나머지 직원 동기화가 막히지 않도록 한다.
export const isValidEmployeeDocId = (name: unknown): name is string => {
  if (typeof name !== 'string' || name.trim() === '') return false;
  if (name.includes('/')) return false;
  if (name === '.' || name === '..') return false;
  if (/^__.*__$/.test(name)) return false;
  return true;
};

// 직원 정보 저장하기
export const saveEmployees = async (employees: any[], deletedEmployeeNames: string[] = []) => {
  try {
    DEBUG && logger.debug('직원 정보 저장 시작', { count: employees.length });
    if (deletedEmployeeNames.length > 0) {
      DEBUG && logger.debug('삭제될 직원 이름', { names: deletedEmployeeNames });
    }

    // 🔧 FIX C11: writeBatch()로 원자적 배치 처리
    const batch = writeBatch(db);
    let operationCount = 0;
    const skippedNames: string[] = [];

    // 1. 현재 직원 데이터 저장 (batch set)
    for (const employee of employees) {
      if (!isValidEmployeeDocId(employee?.name)) {
        skippedNames.push(String(employee?.name ?? '(이름 없음)'));
        continue;
      }
      const employeeRef = doc(db, 'employees', employee.name);
      batch.set(employeeRef, employee, { merge: true });
      operationCount++;
    }

    // 2. 삭제된 직원 처리 (batch delete)
    if (deletedEmployeeNames && deletedEmployeeNames.length > 0) {
      for (const name of deletedEmployeeNames) {
        if (!isValidEmployeeDocId(name)) {
          skippedNames.push(String(name ?? '(이름 없음)'));
          continue;
        }
        const employeeRef = doc(db, 'employees', name);
        batch.delete(employeeRef);
        operationCount++;
        DEBUG && logger.info(`[Firebase] 직원 ${name} 배치 삭제 예약`);
      }
    }

    // 🛡️ 불량 문서 ID로 건너뛴 직원 경고 (배치 전체 실패 방지 목적)
    if (skippedNames.length > 0) {
      logger.warn(
        `Firestore 문서 ID로 쓸 수 없는 이름(빈값/'/'/'.'/'..'/__...__) ${skippedNames.length}건을 건너뜀. ` +
        `이 직원들은 전역 마스터에 반영되지 않습니다: ${skippedNames.join(', ')}`
      );
    }

    // 3. 배치 커밋 (500개 제한 내에서 단일 커밋)
    if (operationCount > 400) {
      logger.warn(`배치 작업 수가 ${operationCount}개로 Firestore 제한(500)에 근접합니다.`);
    }
    if (operationCount > 0) {
      await batch.commit();
    }

    DEBUG && logger.info(' 직원 정보 저장 완료');
  } catch (error) {
    DEBUG && logger.error(' 직원 정보 저장 중 오류:', error);
    throw error;
  }
};

// 실시간 직원 데이터 구독 (선택적)
export const subscribeToEmployees = (callback: (data: any[]) => void) => {
  const employeesCol = collection(db, 'employees');
  return onSnapshot(employeesCol, (snapshot) => {
    const employeesData = snapshot.docs.map(doc => ({
      name: doc.id,
      ...doc.data()
    }));
    callback(employeesData);
  });
};
